import { CSSObject } from '@emotion/react'
import ReactPlayer from 'react-player'
import styled from '@emotion/styled'
import { useEffect, useRef, useState } from 'react'
import { getFirstFrameUrl } from '../../util/path.ts'
import { PlayIcon } from '../atoms/Icons.tsx'
import {
  isHlsFile,
  PREFERRED_HLS_VERSION,
  useBrowserSupportsHls,
} from '../../util/video-util.ts'
import { MediaControl } from '../atoms/MediaControl/MediaControl'
import { VIEWPORT_HEIGHT } from '../../util/viewportHeight'

interface VideoPlayerProps {
  videoUrl: string
  playing: boolean
  controls:
    | 'none'
    | 'browserDefault'
    | 'customWithoutMuteButton'
    | 'customWithMuteButton'
  muted: boolean
  loop: boolean
  alt: string
  subtitleLang: string
  fullscreen?: boolean
  className?: string
  onBufferEnd?: () => void
  onEnded?: () => void
  onMuteChange?: (muted: boolean) => void
}

export const VideoPlayer = (props: VideoPlayerProps) => {
  const browserSupportsHls = useBrowserSupportsHls()

  const [playing, setPlaying] = useState(props.playing)
  const [muted, setMuted] = useState(props.muted)
  const [progressValue, setProgressValue] = useState<number>(0)
  const [duration, setDuration] = useState<number>(0)

  const [showClickTarget, setShowClickTarget] = useState<boolean>(true)
  const [showCustomControls, setShowCustomControls] = useState(false)

  const playerRef = useRef<ReactPlayer>(null)
  const intervalId = useRef<number>()

  // When the playing prop changes, override our internal playing state
  useEffect(() => {
    setPlaying(props.playing)
    setShowCustomControls(props.playing)
  }, [props.playing])

  // When the mute prop changes, override our internal mute state
  useEffect(() => {
    setMuted(props.muted)
  }, [props.muted])

  useEffect(() => {
    // on browserDefault we want show the click target only to grab first click
    if (props.controls === 'browserDefault') {
      if (playing) {
        setShowClickTarget(false)
      }
    }

    // always, since we use it to toggle play/pause
    if (
      props.controls === 'customWithoutMuteButton' ||
      props.controls === 'customWithMuteButton'
    ) {
      setShowClickTarget(true)
    }

    if (props.controls === 'none') {
      setShowClickTarget(false)
    }
  }, [playing, props.controls])

  // Update the progressValue periodically when video is playing
  useEffect(() => {
    if (
      (props.controls === 'customWithoutMuteButton' ||
        props.controls === 'customWithMuteButton') &&
      playing
    ) {
      intervalId.current = window.setInterval(() => {
        if (playerRef.current) {
          const currentTime = playerRef.current.getCurrentTime()
          const duration = playerRef.current.getDuration()

          if (
            !Number.isNaN(currentTime) &&
            !Number.isNaN(duration) &&
            duration > 0
          ) {
            const played = (currentTime / duration) * 100
            setProgressValue(played)
          }
        }
      }, 30)
    } else if (intervalId.current) {
      clearInterval(intervalId.current)
      intervalId.current = undefined
    }

    return () => {
      if (intervalId.current) {
        clearInterval(intervalId.current)
      }
    }
  }, [props.controls, playing])

  const handleProgressClick = (progress: number) => {
    if (playerRef.current && duration > 0) {
      const newTime = (progress / 100) * duration
      playerRef.current.seekTo(newTime, 'seconds')
      setProgressValue(progress)
    }
  }

  const togglePlayPause = () => {
    setPlaying(!playing)
  }

  const updateMuted = (muted: boolean) => {
    setMuted(muted)
    props.onMuteChange?.(muted)
  }

  const toggleMuted = () => {
    updateMuted(!muted)
  }

  const posterUrl = getFirstFrameUrl(props.videoUrl)

  const onVideoPlayError = (error: unknown) => {
    console.error('Could not play video', props.videoUrl, error)

    if (muted) {
      setPlaying(false)
    } else {
      setPlaying(false)
      updateMuted(true)
      setTimeout(() => {
        setPlaying(true)
      }, 100)
    }
  }

  return (
    <VideoContainer fullscreen={props.fullscreen}>
      {showClickTarget && (
        <PlayPauseClickTarget onClick={togglePlayPause}/>
      )}

      {props.controls === 'browserDefault' && (
        <FadeInOutDiv
          visible={showClickTarget}
        >
          <CenteredControlsPanel>
            <PlayIcon width="100px" height="100px" />
          </CenteredControlsPanel>
        </FadeInOutDiv>
      )}

      {(props.controls === 'customWithoutMuteButton' ||
        props.controls === 'customWithMuteButton') && (
        <FadeInOutDiv visible={showCustomControls}>
          <MediaControl
            playing={playing}
            progress={progressValue}
            muted={muted}
            onPlayPause={togglePlayPause}
            onProgressClick={handleProgressClick}
            onMuteToggle={toggleMuted}
            mediaURL={props.videoUrl}
            subtitleLang={props.subtitleLang}
            duration={duration}
            position="absolute"
            controls={{
              playPause: true,
              progress: true,
              mute: props.controls === 'customWithMuteButton',
            }}
            scrollPercentage={null}
          />
        </FadeInOutDiv>
      )}

      <StyledReactPlayer
        ref={playerRef}
        url={props.videoUrl}
        className={props.className ?? ''}
        // Controls should initially be hidden on browserDefault, but then always stay
        controls={props.controls === 'browserDefault' && !showClickTarget}
        playing={playing}
        muted={muted}
        volume={0.75}
        loop={props.loop}
        alt={props.alt}
        width="100%"
        height="100%"
        playsinline={true}
        onPause={() => setPlaying(false)}
        onBufferEnd={props.onBufferEnd}
        onPlay={() => setPlaying(true)}
        onDuration={(duration) => setDuration(duration)}
        onError={onVideoPlayError}
        onEnded={() => {
          setPlaying(false)
          props.onEnded?.()
        }}
        config={{
          file: {
            forceHLS: isHlsFile(props.videoUrl) && !browserSupportsHls,
            hlsVersion: PREFERRED_HLS_VERSION,
            attributes: {
              poster: posterUrl,
            },
          },
        }}
      />
    </VideoContainer>
  )
}

const VideoContainer = styled.div<{ fullscreen?: boolean }>`
  position: relative;
  ${(props): CSSObject =>
    props.fullscreen
      ? {
        position: 'relative',
        width: '100%',
        height: VIEWPORT_HEIGHT,
        video: {
          objectFit: 'cover',
        },
      }
      : {}}
`

const StyledReactPlayer = styled(ReactPlayer)`
  display: flex;
`

const FadeInOutDiv = styled.div<{ visible: boolean }>`
  ${({ visible }) => (visible ? 'opacity: 1;' : 'opacity: 0;')}
  transition: opacity 0.3s ease-in-out;
`

const CenteredControlsPanel = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;

  display: flex;
  flex: 0 0 auto;

  ${props => props.theme.mediaControlsInlineVideoPlayButton}
`

const PlayPauseClickTarget = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: auto;
  z-index: 1;
`
